import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";

/** Messages per page: a chat opens on the latest page and loads older ones on scroll-up. */
export const MESSAGE_PAGE_SIZE = 30;

/** Start loading the previous page this close to the top, so it's usually there in time. */
const LOAD_AHEAD_PX = 200;

const isServerMessage = (m) => !m._optimistic && typeof m.id === "number";

/**
 * Swaps the latest page into what's loaded, keeping older pages. When the latest page doesn't
 * reach back to anything loaded (a burst of new messages), starts over from the latest page.
 * Returns the merged list and whether older messages may remain.
 */
export function mergeLatest(loaded, latest, hadMore) {
    const kept = loaded.filter(isServerMessage);
    if (latest.length === 0) return {messages: kept, hasMore: hadMore};
    const firstLatest = latest[0].id;
    const newestLoaded = kept.reduce((max, m) => (m.id > max ? m.id : max), -Infinity);
    const full = latest.length >= MESSAGE_PAGE_SIZE;
    if (full && firstLatest > newestLoaded) return {messages: latest, hasMore: true};
    return {messages: [...kept.filter((m) => m.id < firstLatest), ...latest], hasMore: hadMore || (kept.length === 0 && full)};
}

/**
 * Paged chat history for one conversation.
 *
 * fetchPage(beforeId) must resolve to up to MESSAGE_PAGE_SIZE messages, oldest first: the latest
 * ones when beforeId is null, otherwise those older than beforeId. A new conversationKey (e.g. the
 * selected tenant) starts over. Attach containerRef and onScroll to the scrolling message box.
 */
export default function useMessagePages(fetchPage, conversationKey) {
    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);

    const containerRef = useRef(null);
    const state = useRef({messages: [], hasMore: false, loadingOlder: false, generation: 0});
    // Where to leave the scroll position after the next render: "bottom", or {height, top} to
    // keep the same messages in view after older ones are added above them.
    const anchor = useRef(null);
    const fetchRef = useRef(fetchPage);
    fetchRef.current = fetchPage;

    const apply = useCallback((next, more) => {
        state.current.messages = next;
        state.current.hasMore = more;
        setMessages(next);
        setHasMore(more);
    }, []);

    const nearBottom = () => {
        const el = containerRef.current;
        return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };

    useEffect(() => {
        if (conversationKey === null || conversationKey === undefined) return;
        const generation = ++state.current.generation;
        state.current.loadingOlder = false;
        setLoadingOlder(false);
        apply([], false);
        setLoading(true);
        Promise.resolve(fetchRef.current(null))
            .then((page) => {
                if (generation !== state.current.generation) return;
                anchor.current = "bottom";
                apply(page, page.length >= MESSAGE_PAGE_SIZE);
            })
            .catch((err) => console.error(err))
            .finally(() => {
                if (generation === state.current.generation) setLoading(false);
            });
    }, [conversationKey, apply]);

    /** Re-fetches the latest page (after sending, or on a poll) without dropping older pages. */
    const refreshLatest = useCallback(() => {
        const generation = state.current.generation;
        return Promise.resolve(fetchRef.current(null)).then((page) => {
            if (generation !== state.current.generation) return;
            if (nearBottom()) anchor.current = "bottom";
            const merged = mergeLatest(state.current.messages, page, state.current.hasMore);
            apply(merged.messages, merged.hasMore);
        });
    }, [apply]);

    const loadOlder = useCallback(() => {
        const s = state.current;
        if (!s.hasMore || s.loadingOlder) return;
        const oldest = s.messages.find(isServerMessage);
        if (!oldest) return;
        const generation = s.generation;
        s.loadingOlder = true;
        setLoadingOlder(true);
        Promise.resolve(fetchRef.current(oldest.id))
            .then((page) => {
                if (generation !== state.current.generation) return;
                const el = containerRef.current;
                if (el) anchor.current = {height: el.scrollHeight, top: el.scrollTop};
                const known = new Set(state.current.messages.map((m) => m.id));
                apply([...page.filter((m) => !known.has(m.id)), ...state.current.messages], page.length >= MESSAGE_PAGE_SIZE);
            })
            .catch((err) => console.error(err)) // hasMore stays set, so scrolling up again retries
            .finally(() => {
                if (generation !== state.current.generation) return;
                state.current.loadingOlder = false;
                setLoadingOlder(false);
            });
    }, [apply]);

    /** Adds a message being sent to the bottom of the chat until the server copy replaces it. */
    const appendOptimistic = useCallback((message) => {
        anchor.current = "bottom";
        apply([...state.current.messages, {...message, _optimistic: true}], state.current.hasMore);
    }, [apply]);

    const dropOptimistic = useCallback(() => {
        apply(state.current.messages.filter((m) => !m._optimistic), state.current.hasMore);
    }, [apply]);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const a = anchor.current;
        anchor.current = null;
        if (a === "bottom") el.scrollTop = el.scrollHeight;
        else if (a) el.scrollTop = el.scrollHeight - a.height + a.top;
        // A short first page may not fill the box, leaving nothing to scroll; fetch more right away.
        if (state.current.hasMore && el.scrollHeight <= el.clientHeight) loadOlder();
    }, [messages, loadOlder]);

    const onScroll = useCallback(() => {
        const el = containerRef.current;
        if (el && el.scrollTop < LOAD_AHEAD_PX) loadOlder();
    }, [loadOlder]);

    return {messages, hasMore, loading, loadingOlder, containerRef, onScroll, refreshLatest, appendOptimistic, dropOptimistic};
}
